import { useState } from 'react';
import { NextPage } from 'next';
import { ButtonLink } from '@zoopla-eng/zds-components/button';
import { Box } from '@zoopla-eng/zds-components/box';

import { withSSROptimizely } from '@zoopla/optimizely/server';
import { Head } from '@zoopla/layout/head';
import { useGetCanonicalPath } from '@zoopla/authentication/helpers';
import {
    SignInForm,
    isNewUser,
    Preferences,
    ActionAfterAuth,
    AuthModalStepKeys,
    YourSituationQuestion,
    shouldShowYourSituation,
    YourSituationResult,
    updateYourSituation,
    UpdateYourSituationType
} from '@zoopla/authentication/modal';
import { useReturnUserAfterAuth } from '@zoopla/authentication/helpers';
import { SignInPageHeader } from './components/SignInPageHeader';
import { NextPageWithApollo } from './SignInPage.types';
import { USER_QUERY } from '@zoopla/authentication/helpers';
import { usePageTracking } from '@zoopla/tracking';
import { User } from '../graphql.generated';
import { useUser, useSetIsLoggedIn } from '@zoopla/user';
import { AuthenticationLayout, InformationLayout } from '@zoopla/layout/page';
import { trackQuestionClose } from './SignInPage.tracking';
import {
    signInPageFormWidthStyle,
    skipLinkContainerStyle
} from './SignInPage.styles.css';

const SignInPage: NextPage = () => {
    usePageTracking('/signin/', {
        section: 'signin'
    });
    const [currentStep, setCurrentStep] = useState<AuthModalStepKeys>(
        AuthModalStepKeys.FORM_TABS
    );
    const { registered } = useUser();
    const setLoggedIn = useSetIsLoggedIn();

    const actionAfterAuth: ActionAfterAuth = {
        action: () => null,
        eventLabel: '/signin/',
        segment: {
            page: '/signin/',
            section: 'signin'
        }
    };

    const canonicalPath = useGetCanonicalPath();
    const { redirectUser, returnUserLinkUrl } = useReturnUserAfterAuth();

    const socialAuthSignIn = async () => {
        setLoggedIn(true);
    };

    // called after SSO
    const socialAuth = async (user?: User) => {
        socialAuthSignIn();

        if (isNewUser({ registered }, user?.creationDate)) {
            sessionStorage.setItem('new_register_user', 'true');
            setCurrentStep(AuthModalStepKeys.PREFERENCES);
            return;
        }

        const userProfileStorage = sessionStorage.getItem('auth-user-profile');
        const userProfileData = JSON.parse(userProfileStorage || '{}');
        const situationStatus = updateYourSituation(userProfileData);

        if (situationStatus === UpdateYourSituationType.SELLER_FOLLOWUP) {
            setCurrentStep?.(AuthModalStepKeys.UPDATE_YOUR_SITUATION_SELLER);
            return;
        }

        if (situationStatus === UpdateYourSituationType.NEEDS_UPDATE) {
            setCurrentStep?.(AuthModalStepKeys.UPDATE_YOUR_SITUATION);
            return;
        }

        if (situationStatus === UpdateYourSituationType.NO_SITUATION_ANSWER) {
            setCurrentStep?.(AuthModalStepKeys.YOUR_SITUATION);
            return;
        }

        if (shouldShowYourSituation(user)) {
            setCurrentStep?.(AuthModalStepKeys.YOUR_SITUATION);
            return;
        }

        redirectUserOrConfirmation(user);
    };

    // called after regular signin
    // or after setting preferences in SSO
    const redirectUserOrConfirmation = (_?: User) => {
        redirectUser();
    };

    const skipSituationQuestion = (_: unknown, lastQuestion = '') => {
        trackQuestionClose(lastQuestion, '/signin/');
        redirectUser();
    };

    const closeSituationQuestion = (_: unknown, lastQuestion = '') => {
        trackQuestionClose(lastQuestion, '/signin/');
    };

    const signInFirstStep = (
        <>
            <SignInPageHeader />
            <noscript>Activate Javascript to sign in</noscript>
            <SignInForm
                close={redirectUserOrConfirmation}
                actionAfterAuth={actionAfterAuth}
                onSocialAuth={socialAuth}
                setCurrentStep={setCurrentStep}
            />
        </>
    );

    const steps: {
        [key in AuthModalStepKeys]?: { component: JSX.Element };
    } = {
        formTabs: { component: signInFirstStep },
        preferences: {
            component: (
                <>
                    <Preferences
                        close={redirectUserOrConfirmation}
                        actionAfterAuth={actionAfterAuth}
                    />
                    <div className={skipLinkContainerStyle}>
                        <ButtonLink href={returnUserLinkUrl}>
                            Skip and set later in Email preferences
                        </ButtonLink>
                    </div>
                </>
            )
        },
        yourSituation: {
            component: (
                <YourSituationQuestion
                    page="/signin/"
                    onClose={skipSituationQuestion}
                    trackingIdentifier=""
                    title="Customise your experience"
                    actionElement={
                        <YourSituationResult.Link
                            href={returnUserLinkUrl}
                            onClick={closeSituationQuestion}
                        />
                    }
                />
            )
        },
        updateYourSituation: {
            component: (
                <YourSituationQuestion
                    questionType="update"
                    trackingIdentifier="update: "
                    page="/signin/"
                    onClose={skipSituationQuestion}
                    title="Update your situation"
                    actionElement={
                        <YourSituationResult.Link
                            href={returnUserLinkUrl}
                            onClick={closeSituationQuestion}
                        />
                    }
                />
            )
        },
        updateYourSituationSeller: {
            component: (
                <YourSituationQuestion
                    questionType="seller"
                    trackingIdentifier="update: "
                    page="/signin/"
                    onClose={skipSituationQuestion}
                    title="Update your situation"
                    actionElement={
                        <YourSituationResult.Link
                            href={returnUserLinkUrl}
                            onClick={closeSituationQuestion}
                        />
                    }
                />
            )
        }
    };

    const isSituationQuestions =
        currentStep === AuthModalStepKeys.YOUR_SITUATION ||
        currentStep === AuthModalStepKeys.UPDATE_YOUR_SITUATION ||
        currentStep === AuthModalStepKeys.UPDATE_YOUR_SITUATION_SELLER;

    return (
        <>
            <Head
                pageTitle="Sign in to Zoopla  - Zoopla"
                pageDescription="Sign in to save properties and much more"
                canonicalPath={canonicalPath}
            >
                <noscript>
                    <style>
                        {`form, .social-login {
                            display: none !important;
                        }`}
                    </style>
                </noscript>
            </Head>
            {isSituationQuestions && (
                <InformationLayout
                    main={
                        <Box pt="S500" bpL100={{ pt: 'M300' }} fw>
                            <div
                                className={signInPageFormWidthStyle({
                                    larger: true
                                })}
                            >
                                {steps[currentStep]?.component}
                            </div>
                        </Box>
                    }
                />
            )}
            {!isSituationQuestions && (
                <AuthenticationLayout
                    main={
                        <Box pt="S500" bpL100={{ pt: 'M300' }} fw>
                            <div className={signInPageFormWidthStyle()}>
                                {steps[currentStep]?.component}
                            </div>
                        </Box>
                    }
                />
            )}
        </>
    );
};

SignInPage.getInitialProps = withSSROptimizely.getInitialProps(
    async ({ apolloClient, req, res }: NextPageWithApollo) => {
        if (typeof window === 'undefined') {
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const newrelic = require('newrelic');
            newrelic.setTransactionName('/signin/');
        }

        if (req && res) {
            const { data, errors } = await apolloClient.query({
                query: USER_QUERY
            });

            if (!errors && data?.user?.loggedIn) {
                res.writeHead(302, { Location: '/my-zoopla/' });
                res.end();
            }
        }

        return {};
    }
);

export default SignInPage;
